import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Inbox, Users, MessageSquare, TrendingUp, Loader2, Activity, Eye, FileBarChart,
  Target, Calendar, Sparkles, ArrowRight, BarChart3,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Cell,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function KpiCard({ title, value, sub, icon: Icon, accent = "cyan" }) {
  const accents = {
    cyan: "from-cyan-500/10 to-cyan-500/0 ring-cyan-500/20 text-cyan-700",
    indigo: "from-indigo-500/10 to-indigo-500/0 ring-indigo-500/20 text-indigo-700",
    emerald: "from-emerald-500/10 to-emerald-500/0 ring-emerald-500/20 text-emerald-700",
    amber: "from-amber-500/10 to-amber-500/0 ring-amber-500/20 text-amber-700",
    rose: "from-rose-500/10 to-rose-500/0 ring-rose-500/20 text-rose-700",
  };
  return (
    <div className={`bg-white rounded-xl border border-slate-200 p-4 relative overflow-hidden`}>
      <div className={`absolute inset-0 bg-gradient-to-br opacity-60 ${accents[accent].split(" ")[0]} ${accents[accent].split(" ")[1]} pointer-events-none`} />
      <div className="relative">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            {title}
          </span>
          <Icon className={`w-4 h-4 ${accents[accent].split(" ")[3]}`} />
        </div>
        <div className="text-2xl font-bold text-slate-900">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
      </div>
    </div>
  );
}

export default function AdminOverviewPage() {
  const [data, setData] = useState(null);
  const [crm, setCrm] = useState(null);
  const [crmReports, setCrmReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ov, crmRes, crmRep] = await Promise.all([
          axios.get(`${API}/admin/overview`),
          axios.get(`${API}/admin/crm/dashboard`).catch(() => ({ data: null })),
          axios.get(`${API}/admin/crm/reports`).catch(() => ({ data: null })),
        ]);
        if (!cancelled) {
          setData(ov.data);
          setCrm(crmRes.data);
          setCrmReports(crmRep.data);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load overview");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading overview…
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
        {error}
      </div>
    );
  }

  const c = data.contacts;
  const v = data.visitors;
  const a = data.assistant;

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Overview</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Real-time KPIs from your live website.
        </p>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <KpiCard title="Page Views (Total)" value={v.page_views_total} sub={`${v.page_views_today} today`} icon={Eye} accent="cyan" />
        <KpiCard title="Unique Visitors (Total)" value={v.unique_total} sub={`${v.unique_today} today`} icon={Users} accent="indigo" />
        <KpiCard title="Visitors (7d)" value={v.unique_week} sub={`${v.page_views_week} page views`} icon={Activity} accent="emerald" />
        <KpiCard title="Conversion Rate (30d)" value={`${data.conversion_rate_30d_pct}%`} sub="contacts ÷ unique visitors" icon={TrendingUp} accent="amber" />

        <KpiCard title="Contact Requests" value={c.total} sub={`${c.new} new`} icon={Inbox} accent="cyan" />
        <KpiCard title="Contacts Today" value={c.today} sub={`${c.week} this week`} icon={Inbox} accent="rose" />
        <KpiCard title="Assistant Conversations" value={a.conversations} sub={`${a.messages} user messages`} icon={MessageSquare} accent="indigo" />
        <KpiCard title="Most Visited Page" value={data.most_visited_page || "—"} icon={FileBarChart} accent="emerald" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Page Views — last 14 days" data={data.visitors_series} color="#0891b2" />
        <ChartCard title="Contact Submissions — last 14 days" data={data.submissions_series} color="#0e7490" />
      </div>

      {/* CRM Section */}
      {crm && <CrmSection crm={crm} reports={crmReports} />}
    </div>
  );
}

const STATUS_COLORS = {
  New: "#94a3b8",
  Reviewed: "#64748b",
  Interested: "#10b981",
  DemoSent: "#06b6d4",
  FollowUpNeeded: "#f59e0b",
  PilotCandidate: "#6366f1",
  Converted: "#059669",
  NotInterested: "#ef4444",
};

function CrmSection({ crm, reports }) {
  return (
    <div className="space-y-4 pt-2" data-testid="overview-crm-section">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold text-slate-900 font-['Plus_Jakarta_Sans']">CRM at a glance</h2>
          <p className="text-xs text-slate-500">Live snapshot of your sales / partnership pipeline.</p>
        </div>
        <Link to="/admin/crm/contacts" className="text-xs font-semibold text-cyan-700 hover:text-cyan-900 inline-flex items-center gap-1" data-testid="overview-crm-link">
          Open CRM <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          title="Active leads"
          value={crm.active_leads}
          sub={`${crm.new_this_week} new this week`}
          icon={Sparkles}
          accent="indigo"
        />
        <KpiCard
          title="Follow-ups due"
          value={crm.follow_ups_due}
          sub={crm.follow_ups_overdue > 0 ? `${crm.follow_ups_overdue} overdue` : "all on schedule"}
          icon={Calendar}
          accent={crm.follow_ups_overdue > 0 ? "rose" : "amber"}
        />
        <KpiCard
          title="Pilot candidates"
          value={crm.pilot_candidates}
          sub="incl. PilotPartner / pilot_inquiry tag"
          icon={Target}
          accent="cyan"
        />
        <KpiCard
          title="New (7d)"
          value={crm.new_this_week}
          sub="created this week"
          icon={TrendingUp}
          accent="emerald"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent activity */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-4" data-testid="overview-crm-activity">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700">Recent CRM activity</h3>
            <span className="text-xs text-slate-400">{crm.recent_activities?.length || 0} latest</span>
          </div>
          {!crm.recent_activities || crm.recent_activities.length === 0 ? (
            <div className="text-xs text-slate-400 text-center py-6">No CRM activity yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {crm.recent_activities.map((a) => (
                <li key={a.id} className="py-2 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-cyan-50 ring-1 ring-cyan-200 flex items-center justify-center mt-0.5">
                    <Activity className="w-3.5 h-3.5 text-cyan-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-800 truncate">
                      <span className="font-medium">{a.title || a.type}</span>
                      {a.contact && (
                        <Link to={`/admin/crm/contacts/${a.contact_id}`} className="ml-1 text-cyan-700 hover:underline">
                          · {a.contact.name}
                        </Link>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      <span className="uppercase tracking-wider text-[10px] font-semibold text-slate-400 mr-2">{a.type}</span>
                      {new Date(a.created_at).toLocaleString()}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Top categories */}
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="overview-crm-categories">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Top categories</h3>
          {!crm.top_categories || crm.top_categories.length === 0 ? (
            <div className="text-xs text-slate-400 text-center py-6">No categories yet.</div>
          ) : (
            <ul className="space-y-2">
              {crm.top_categories.map((c) => {
                const max = Math.max(...crm.top_categories.map((x) => x.count));
                return (
                  <li key={c.key} className="text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-700 truncate">{c.key}</span>
                      <span className="text-xs font-semibold text-slate-500 tabular-nums">{c.count}</span>
                    </div>
                    <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-cyan-500/70" style={{ width: `${max ? (c.count / max) * 100 : 0}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Funnel mini-widget */}
      {reports?.status_funnel && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="overview-crm-funnel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-slate-500" />
              <h3 className="text-sm font-semibold text-slate-700">Pipeline funnel</h3>
            </div>
            <Link
              to="/admin/crm/reports"
              className="text-xs font-semibold text-cyan-700 hover:text-cyan-900 inline-flex items-center gap-1"
              data-testid="overview-crm-funnel-link"
            >
              Open reports <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div style={{ width: "100%", height: 160 }}>
            <ResponsiveContainer>
              <BarChart
                data={reports.status_funnel}
                margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="stage" tick={{ fontSize: 9, fill: "#64748b" }} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 9, fill: "#64748b" }} allowDecimals={false} width={28} />
                <Tooltip wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {reports.status_funnel.map((entry) => (
                    <Cell key={entry.stage} fill={STATUS_COLORS[entry.stage] || "#0891b2"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          {reports.totals && (
            <div className="mt-2 text-[11px] text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
              <span>Conversion rate: <strong className="text-slate-700">{reports.totals.conversion_rate}%</strong></span>
              <span>Outreach (30d): <strong className="text-slate-700">{reports.totals.outreach_30d}</strong></span>
              <span>AI drafted (30d): <strong className="text-slate-700">{reports.totals.ai_generated_30d}</strong></span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ChartCard({ title, data, color }) {
  const safe = (data && data.length) ? data : [{ date: new Date().toISOString().slice(0, 10), count: 0 }];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h2 className="text-sm font-semibold text-slate-700 mb-3">{title}</h2>
      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer>
          <AreaChart data={safe} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
            <defs>
              <linearGradient id={`grad-${title}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.4} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#f1f5f9" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748b" }} />
            <YAxis tick={{ fontSize: 10, fill: "#64748b" }} allowDecimals={false} />
            <Tooltip wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="count" stroke={color} strokeWidth={2} fill={`url(#grad-${title})`} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
